import { UserMediaContext } from "@/Components/Providers";
import { getRandomNumber, mediaRecorderErrors, useAppTranslation, useMediaRecorder } from "@/utils";
import { PhRPause, PhRPlay, PhRRecord, PhRRepeat } from "@payping/icons";
import {
  Button,
  Card,
  Flex,
  Grid,
  Modal,
  Typography,
  notification as notificationComponent,
} from "@payping/web-components";
import { clsx } from "clsx";
import { FC, useContext, useEffect, useRef, useState } from "react";

interface StartProps {
  onFinish: (file: File, sentence: string) => void;
  notification: typeof notificationComponent;
  loading?: boolean;
}
const GetOrderUploadVideoAndSignaturePageStart: FC<StartProps> = ({ notification, onFinish, loading }) => {
  const { t } = useAppTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const recordVideoRef = useRef<HTMLVideoElement>(null);
  const [countDown, setCountDown] = useState(15);
  const [doneRecording, setDoneRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // const [videoUrl, setVideoUrl] = useState<string>();
  const countDownTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const { mediaStream } = useContext(UserMediaContext);
  const { lg } = Grid.useBreakpoint();
  const allSentence: string[] = t("videoAuthenticationSentences", { returnObjects: true }) as string[];
  const [randomSentence, setRandomSentence] = useState(allSentence[getRandomNumber(allSentence.length - 1)]);

  const mp4MediaRecorder = useMediaRecorder({
    mediaStream,
    mimeType: "video/mp4",
  });

  const [sendModalShow, setSendModalShow] = useState(false);

  useEffect(() => {
    if (recordVideoRef.current && mediaStream && !doneRecording) {
      recordVideoRef.current.srcObject = mediaStream;
      recordVideoRef.current.setAttribute("playsinline", "true");
      recordVideoRef.current.play();
    }
  }, [doneRecording, mediaStream]);

  // handle errors
  useEffect(() => {
    if (mp4MediaRecorder.error === mediaRecorderErrors.INVALID_MIME_TYPE_SUPPORT) {
      notification.error({ message: t("hooks.mediaRecorder.INVALID_MIME_TYPE_SUPPORT") });
    }
    if (mp4MediaRecorder.error === mediaRecorderErrors.NO_MEDIA_STREAM_AVAILABLE) {
      notification.error({ message: t("hooks.mediaRecorder.NO_MEDIA_STREAM_AVAILABLE") });
    }
    if (mp4MediaRecorder.error === mediaRecorderErrors.RECORDER_API_NOT_SUPPORTED_IN_BROWSER) {
      notification.error({ message: t("hooks.mediaRecorder.RECORDER_API_NOT_SUPPORTED_IN_BROWSER") });
    }
    if (mp4MediaRecorder.error === mediaRecorderErrors.RECORDING_FAILED) {
      notification.error({ message: t("hooks.mediaRecorder.RECORDING_FAILED") });
    }
  }, [notification, t, mp4MediaRecorder.error]);

  const handleStopRecording = () => {
    mp4MediaRecorder.stopRecording();
    clearInterval(countDownTimer.current);
    setCountDown(15);
    setDoneRecording(true);
  };

  const handleStartRecording = () => {
    if (mp4MediaRecorder.error) {
      return;
    }
    setDoneRecording(false);
    mp4MediaRecorder.startRecording();
    countDownTimer.current = setInterval(() => {
      setCountDown((pre) => {
        if (pre === 1) {
          mp4MediaRecorder.stopRecording();
          clearInterval(countDownTimer.current);
          setDoneRecording(true);
          return 15;
        }
        return pre - 1;
      });
    }, 1000);
  };

  const handlePlayVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsPlaying(true);
    video.play();
  };

  const handleStopVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    setIsPlaying(false);
    video.pause();
    video.currentTime = 0;
  };

  const handleDoneVideoRecord = () => {
    if (mp4MediaRecorder.recordedFile) {
      onFinish(mp4MediaRecorder.recordedFile, randomSentence);
    }
  };

  const handleRetryRecordVideo = () => {
    handleStopVideo();
    setRandomSentence(allSentence[getRandomNumber(allSentence.length - 1)]);
    setDoneRecording(false);
  };

  return (
    <>
      <Flex vertical className="tw:relative tw:h-full" gap={"middle"}>
        <Flex className="tw:relative tw:mt-8! tw:w-full" vertical>
          <Flex justify="center" className={clsx(["tw:px-auto tw:absolute -tw:top-8 tw:z-10 tw:w-full"])}>
            <Card className="tw:w-full tw:max-w-72.5 tw:text-center" size="small">
              <Typography.Title level={5} className="tw:m-0!">
                {randomSentence}
              </Typography.Title>
            </Card>
          </Flex>
          <video
            ref={videoRef}
            src={mp4MediaRecorder.recordedFileUrl ?? ""}
            playsInline
            style={{
              transform: "scaleX(-1)",
              display: doneRecording && !!mp4MediaRecorder.recordedFileUrl ? undefined : "none",
            }}
            className="tw:w-full"
            onEnded={() => {
              setIsPlaying(false);
            }}
          />

          <video
            ref={recordVideoRef}
            muted
            autoPlay
            playsInline
            style={{
              transform: "scaleX(-1)",
              display: doneRecording && !!mp4MediaRecorder.recordedFileUrl ? "none" : undefined,
            }}
            className="tw:w-full"
          />

          <svg
            className="tw:absolute"
            width="100%"
            height="100%"
            viewBox="0 0 500 500"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="
    M 0 0 H 500 V 500 H 0 Z
    M 250 50 A 200 200 0 1 1 249.9 50 Z
  "
              fill="rgba(0,0,0,0.5)"
              fillRule="evenodd"
            />
          </svg>
          <Flex className="tw:absolute tw:bottom-4 tw:left-4">
            {!doneRecording ? <Typography.Text color="white">{`00:${countDown}`}</Typography.Text> : null}
            {doneRecording && !isPlaying ? (
              <Button icon={<PhRPlay width={14} height={14} />} onClick={handlePlayVideo} ghost>
                {t("common.play")}
              </Button>
            ) : null}
            {doneRecording && isPlaying ? (
              <Button icon={<PhRPause width={14} height={14} />} onClick={handleStopVideo} ghost>
                {t("common.pause")}
              </Button>
            ) : null}
          </Flex>
        </Flex>

        {!doneRecording ? (
          <Flex
            gap={"middle"}
            className={clsx({
              "tw:mt-auto": lg,
            })}
          >
            {mp4MediaRecorder.isRecording && mp4MediaRecorder.isRecording ? (
              <Button icon={<PhRRecord width={14} height={14} />} block danger onClick={handleStopRecording}>
                {t("pay.getOrder.steps.uploadVideoAndSignature.stopRecording")}
              </Button>
            ) : (
              <Button
                disabled={!!mp4MediaRecorder.error}
                icon={<PhRRecord width={14} height={14} />}
                block
                danger
                type="primary"
                onClick={handleStartRecording}
              >
                {t("pay.getOrder.steps.uploadVideoAndSignature.startRecording")}
              </Button>
            )}
          </Flex>
        ) : null}
        {doneRecording ? (
          <Flex gap={"middle"} className={"tw:mt-auto"}>
            <Button
              block
              type="primary"
              onClick={() => {
                setSendModalShow(true);
              }}
            >
              {t("common.send")}
            </Button>
            <Button
              block
              icon={<PhRRepeat width={14} height={14} />}
              danger
              type="text"
              onClick={handleRetryRecordVideo}
            >
              {t("common.retry")}
            </Button>
          </Flex>
        ) : null}
      </Flex>
      {sendModalShow ? (
        <Modal
          open={sendModalShow}
          onCancel={() => {
            if (loading) return;
            setSendModalShow(false);
          }}
          title={t("pay.getOrder.steps.uploadVideoAndSignature.confirmSendModal.title")}
          footer={
            <Flex align="center" gap={"small"}>
              <Button
                disabled={loading}
                onClick={() => {
                  setSendModalShow(false);
                }}
              >
                {t("pay.getOrder.steps.uploadVideoAndSignature.confirmSendModal.cancel")}
              </Button>
              <Button loading={loading} onClick={handleDoneVideoRecord} type="primary">
                {t("pay.getOrder.steps.uploadVideoAndSignature.confirmSendModal.ok")}
              </Button>
            </Flex>
          }
        >
          <Flex className={clsx([{ "tw:h-24": lg }])}>
            <Typography.Text>
              {t("pay.getOrder.steps.uploadVideoAndSignature.confirmSendModal.content")}
            </Typography.Text>
          </Flex>
        </Modal>
      ) : null}
    </>
  );
};

export default GetOrderUploadVideoAndSignaturePageStart;
